import { useCallback, useEffect, useState } from "react";
import { Archive, ArchiveRestore, Inbox, MoreVertical, Pencil, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

type SpendCategory = components["schemas"]["SpendCategoryResponse"];
type SpendClass = components["schemas"]["SpendClass"];

const SPEND_CLASS_NONE = "__none__";

function spendClassLabel(c: SpendClass): string {
  return c ?? "—";
}

export function SpendCategoriesSection({
  clientId,
  isAdmin,
}: {
  clientId: string;
  isAdmin: boolean;
}) {
  const [categories, setCategories] = useState<SpendCategory[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SpendCategory | null>(null);

  const load = useCallback(
    async (signal?: { cancelled: boolean }) => {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/v1/client/spend-categories");
      if (signal?.cancelled) return;
      if (apiError || !data) {
        setError("Failed to load spend categories.");
        setLoading(false);
        return;
      }
      setCategories(data);
      setLoading(false);
    },
    [clientId],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    void load(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [load]);

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(category: SpendCategory) {
    setEditing(category);
    setDialogOpen(true);
  }

  async function toggleArchive(category: SpendCategory) {
    const { error: apiError } = await api.PATCH("/v1/client/spend-categories/{id}", {
      params: { path: { id: category.id } },
      body: {
        displayName: category.displayName,
        defaultSpendClass: category.defaultSpendClass,
        glAccountCode: category.glAccountCode,
        isArchived: !category.isArchived,
      },
    });
    if (apiError) {
      toast.error("Couldn't update category.");
      return;
    }
    toast.success(category.isArchived ? "Category restored" : "Category archived");
    void load();
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>Bill spend categories</CardTitle>
          <CardDescription>
            Custom categories bills can be classified against. These cascade to every organisation
            under this client and are offered alongside Dolfin's system defaults.
            {!isAdmin && " You need Admin or Owner role to manage these."}
          </CardDescription>
        </div>
        {isAdmin && (
          <Button size="sm" onClick={openCreate} disabled={loading}>
            <Plus className="size-3.5" /> Add category
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
            <Spinner className="size-4" /> Loading categories…
          </div>
        ) : error ? (
          <p className="py-6 text-sm text-destructive">{error}</p>
        ) : !categories || categories.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border py-10 text-center text-sm text-muted-foreground">
            <Inbox className="size-6" />
            No custom categories yet. Bills still classify against Dolfin's system defaults.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead>Category</TableHead>
                  <TableHead>Key</TableHead>
                  <TableHead>Default spend class</TableHead>
                  <TableHead>GL code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((c) => (
                  <TableRow key={c.id} className={c.isArchived ? "opacity-60" : undefined}>
                    <TableCell className="font-medium">{c.displayName}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{c.key}</TableCell>
                    <TableCell>{spendClassLabel(c.defaultSpendClass)}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.glAccountCode ?? "—"}
                    </TableCell>
                    <TableCell>
                      {c.isArchived ? (
                        <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                          Archived
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="border-secondary/50 bg-secondary/15 text-secondary-foreground">
                          Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {isAdmin && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon-sm">
                              <MoreVertical className="size-3.5" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(c)}>
                              <Pencil className="size-3.5" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => toggleArchive(c)}>
                              {c.isArchived ? (
                                <>
                                  <ArchiveRestore className="size-3.5" /> Restore
                                </>
                              ) : (
                                <>
                                  <Archive className="size-3.5" /> Archive
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {isAdmin && (
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <CategoryDialog
            key={editing?.id ?? "new"}
            clientId={clientId}
            category={editing}
            onClose={() => setDialogOpen(false)}
            onSaved={() => {
              setDialogOpen(false);
              void load();
            }}
          />
        </Dialog>
      )}
    </Card>
  );
}

function CategoryDialog({
  clientId,
  category,
  onClose,
  onSaved,
}: {
  clientId: string;
  category: SpendCategory | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = category !== null;
  const [key, setKey] = useState(category?.key ?? "");
  const [displayName, setDisplayName] = useState(category?.displayName ?? "");
  const [spendClass, setSpendClass] = useState<SpendClass>(category?.defaultSpendClass ?? null);
  const [glAccountCode, setGlAccountCode] = useState(category?.glAccountCode ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const trimmedName = displayName.trim();
    if (!trimmedName) {
      setError("Display name is required.");
      return;
    }
    const gl = glAccountCode.trim() === "" ? null : glAccountCode.trim();

    setSubmitting(true);
    if (isEdit) {
      const { error: apiError } = await api.PATCH("/v1/client/spend-categories/{id}", {
        params: { path: { id: category.id } },
        body: {
          displayName: trimmedName,
          defaultSpendClass: spendClass,
          glAccountCode: gl,
          isArchived: category.isArchived,
        },
      });
      setSubmitting(false);
      if (apiError) {
        setError((apiError as { detail?: string } | undefined)?.detail ?? "Couldn't update category.");
        return;
      }
      toast.success("Category updated");
      onSaved();
      return;
    }

    const trimmedKey = key.trim();
    if (!trimmedKey) {
      setSubmitting(false);
      setError("Key is required.");
      return;
    }
    const { error: apiError, response } = await api.POST("/v1/client/spend-categories", {
      body: {
        key: trimmedKey,
        displayName: trimmedName,
        defaultSpendClass: spendClass,
        glAccountCode: gl,
      },
    });
    setSubmitting(false);
    if (apiError) {
      const detail = (apiError as { detail?: string } | undefined)?.detail;
      if (response?.status === 409) {
        setError(detail ?? "A category with this key already exists, or it collides with a system default.");
      } else {
        setError(detail ?? "Couldn't create category.");
      }
      return;
    }
    toast.success("Category created");
    onSaved();
  }

  return (
    <DialogContent className="max-w-md">
      <form onSubmit={submit}>
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit spend category" : "Add spend category"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "The key is immutable. Rename, set defaults, or archive this category."
              : "The key is normalised (trimmed, lower-cased, spaces → underscores) and can't shadow a Dolfin default."}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="cat-key">Key</Label>
            <Input
              id="cat-key"
              placeholder="ai_intelligence"
              value={isEdit ? category.key : key}
              onChange={(e) => setKey(e.target.value)}
              disabled={isEdit || submitting}
              className="font-mono"
              autoFocus={!isEdit}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="cat-name">Display name</Label>
            <Input
              id="cat-name"
              placeholder="AI / Intelligence"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={submitting}
              autoFocus={isEdit}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="cat-spend-class">Default spend class</Label>
            <Select
              value={spendClass ?? SPEND_CLASS_NONE}
              onValueChange={(v) => setSpendClass(v === SPEND_CLASS_NONE ? null : (v as SpendClass))}
              disabled={submitting}
            >
              <SelectTrigger id="cat-spend-class">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SPEND_CLASS_NONE}>None</SelectItem>
                <SelectItem value="Fixed">Fixed</SelectItem>
                <SelectItem value="Variable">Variable</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="cat-gl">GL account code</Label>
            <Input
              id="cat-gl"
              placeholder="Optional"
              value={glAccountCode}
              onChange={(e) => setGlAccountCode(e.target.value)}
              disabled={submitting}
              className="font-mono"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting && <Spinner className="size-4" />}
            {isEdit ? "Save changes" : "Create category"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}
